import { gql } from '@apollo/client';

export const GET_USERS = gql`
  query GetUsers {
    users {
      id
      name
      email
    }
  }
`;

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface GetUsersResponse {
  users: User[];
}

export const GET_POSTS_BY_USER_ID = gql`
  query GetPostsByUserId($userId: ID!) {
    user(id: $userId) {
      posts {
        id
        title
        body
      }
    }
  }
`;

export const GET_HELLO = gql`
    query {
  optimizely {
    getHeadlessConfig(language: "en") {
      brand
      country
      url
    }
  }
}
`;

export const REFRESH_TOKEN = gql`
query {
  authentication {
    refreshToken
  }
}`;

export const VALIDATE_TOKEN = gql`
  query ValidateToken($token: String) {
    authentication {
      validateToken (token: $token)
    }
}`;

export const GET_FAVORITES = gql`
  query {
    favorites {
      list {
        userId
        entityId
        entityTypeKey
      }
    }
}`;

export const ADD_FAVORITE = gql`
  query AddFavorite($id: String){
      favorites {
        add(id: $id)
      }
}`;

export const BULK_ADD_FAVORITE = gql`
  query BulkAdd($ids: [String]){
    favorites {
    bulkAdd (ids: $ids)
    }
}`;

export const EXISTS_FAVORITE = gql`
  query ExistsFavorite($id: String){
    favorites {
      exists (id:$id)
    }
}`;

export const DELETE_FAVORITE = gql`
  query DeleteFavorite($id: String){
    favorites {
    delete (id: $id)
    }
}`;

export const BULK_DELETE_FAVORITE = gql`
  query BulkDelete($ids: [String]){
    favorites {
      bulkDelete (ids: $ids)
    }
}`;

export const GET_HEADER = gql`
query Header($url: String, $simplifiedHeader: Boolean, $language: String) {
  header(url: $url, simplifiedHeader: $simplifiedHeader, language: $language) {
    simplifiedHeader
    homePageLink,
    primaryNavigation {
      themeClass
      linkUrl
      current
      multiplePanels
      active
      navigationLinks {
        title
        url
        target
        active
      }
      navigationItems {
        title
        active
        isLitmos
        url
        doormatColumns {
          type
        }
      }
    }
    tertiaryNavigation {
      title
      url
      target
      active
    }
  }
}`;

export const GET_FOOTER = gql`
  query Footer($url: String, $simplifiedFooter: Boolean, $language: String) {
    footer(url: $url, simplifiedFooter: $simplifiedFooter, language: $language) {
      simplifiedFooter
      column1 {
        title
        text
        displayOnMobile
        links {
          title
          url
          target
          active
        }
      }
      column2 {
        title
        text
        displayOnMobile
        links {
          title
          url
          target
          active
        }
      }
      column3 {
        title
        text
        displayOnMobile
        links {
          title
          url
          target
          active
        }
      }
      column4 {
        title
        text
        displayOnMobile
        links {
          title
          url
          target
          active
        }
      }
      socials {
        followUsLabel
        facebookUrl
        instagramUrl
        twitterUrl
        whatsappUrl
        linkedInUrl
        pinterestUrl
        youtubeUrl
        socialText
      }
      textItems
      links {
        title
        url
        target
        active
      }
      footerLogo
      footerLogoLink
    }
  }
`;

export const GET_SEARCH = gql`
   query Search($search: FilterInput) {
  search(search: $search, language: "en") {
    favoriteIds
    isAuthenticated
    count
    currentPage
    isLastPage
    pageCount
    pageSize
    totalCount
    enableAddToSpecPack
    enableItemSelect
    sortByAscending
    enableExport
    enableCopyNameButton
    enableQuickView
    listerFields {
      ...attributeFieldInfoFields
    }
    gridFeaturedFields {
      ...attributeFieldInfoFields
    }
    gridPerformanceFields {
      ...attributeFieldInfoFields
    }
    quickViewFields {
      ...attributeFieldInfoFields
    }
    attributesForSortingDropdown {
      ...attributeFieldInfoFields
    }
    sortFilter {
      ascending
      sortName
    }
    list {
      id
      images
      brand
      category
      name
      properties {
        key
        value
        displayName
      }
    }
    selectFilters {
      filters {
        value
        fieldName
        filterType
        includedInTopFilter
      }
      selectedExclusiveTopFilter
      selectedNonExclusiveTopFilter
      selectedTextQueries
      favoritesTopFilterEnabled
    }
    facets {
      name
      description
      displayName
      values {
        displayName
        key
        countText
      }
    }
    topFilterListExclusive {
      filterDisplayLabel
      prefilterItems {
        attributeConfig {
          ...imsSystemNameAttributeFields
        }
        values
      }
    }
    topFilterListNonExclusive {
      filterDisplayLabel
      prefilterItems {
        attributeConfig {
          ...imsSystemNameAttributeFields
        }
        values
      }
    }
    titleAttribute {
      ...imsSystemNameAttributeFields
    }
    sortBy {
      ...imsSystemNameAttributeFields
    }
  }
}

fragment attributeFieldInfoFields on AttributeFieldInfo {
  name
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  label
  color
  contentForInfoPanel {
    staticContent
  }
}

fragment imsSystemNameAttributeFields on ImsSystemNameAttributeConfig {
  name
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixType
  unitOfMeasure
  label
  labelShort
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
}
`;

export const GET_SITE_CONFIGURATION = gql`
{
  optimizely {
    getHeadlessConfig(language: "en") {
      brand
      country
      url
      smartSystemSelectorContent {
        favoritesTopFilter
        loginUrl
        enableAddToSpecPack
        enableItemSelect
        sortByAscending
        enableExport
        enableCopyNameButton
        enableQuickView
        titleAttribute {
      		...imsSystemNameAttributeConfigFields
        }
        sortBy {
          ...imsSystemNameAttributeConfigFields
        }
        categories {
          ...smartSystemCategoryPageFields
          ... on ImsCategory {
            __typename
            entityType
            categoryCode
            detailPage {
              ... on ImsCategoryDetailPage {
                content {
                  items {
                    fields {
                      name
                      value
                      mimeType
                    }
                    contentItem {
                      __typename
                    }
                  }
                }
              }
              ... on ImsCategoryDetailPage {
                __typename
                content {
                  items {
                    componentName
                    fields {
                      name
                      value
                      mimeType
                    }
                  }
                }
              }
            }
            disableExport
          }
          ... on ImsSubcategory {
            __typename
            subcategoryCode
            code
          }
        }
        facetMapConfig {
          hideEmptyFacets
          hideSingleValueFacets
          facetMapSection {
            facetConfigurations {
              ...smartFacetSliderConfigurationFields
              ...NumberFacetConfigFields
              ...RangeSliderFacetConfigFields
              ...FacetConfigFields
            }
          }
        }
        systemSelectorLabels {
          toLabel
          rangeDescriptionFormatLabel
          allCategoriesLabel
          categoriesLabelMobile
          mainCategoryLabelPrefix
          advancedFiltersLabel
          advancedFiltersApplyLabel
          advancedFiltersMinimumLabel
          advancedFiltersMaximumLabel
          advancedFiltersSelectPlaceholder
          clearSearchResults
          itemsPerPage
          numberOfResultsForSearchText
          noResultTile
          noResultSubTile
          sortByPopular
          sortByCreatedDate
          name
          shortDescription
          emptyValueReplacer
          numberOfEntitiesSelected
          compareButtonLabel
          compareButtonTooltip
          cancelButtonLabel
          stickyToastMessage
        }
        smartListerLabels {
          numberOfResults
          loadMore
          searchTitle
          searchPlaceholder
          searchInfo
          searchButtonLabel
          clearButtonTooltip
          filters
          hideFilters
          showMore
          showLess
          resetFilters
          showResults
          clearAll
          selectAll
          previous
          currentPage
          paginationItems
          quickView
          addToSpec
          copyHover
          saveSystemFavoritesHover
          unSaveSystemFavoritesHover
          copyToClipboardToastMessage
          closeButtonHoverText
          technicalPropertiesTitle
          viewProductDetail
        }
        aiSearchLabels {
          searchWindowTitle
          conversationWindowPlaceholder
          conversationExample1
          conversationExample2
          conversationExample3
          conversationInputPlaceholder1
          conversationInputPlaceholder2
          identifiedFiltersConversationPart
          identifiedFiltersMessage
          sendButton
          showResultsButton
          resetButton
          conversationContinuationQuestion
        }
        enableItemSelect
        enableCopyTechnicalName
        enableQuickView
        enableFavorites
        enableAddToSpecPack
        __typename
      }
    }
  }
}

fragment smartSystemCategoryPageFields on ImsSmartSystemCategory {
  title
  specificationsForTable {
    ...smartAttributesContentAreaFields
  }
  attributesToShowOnListerGridPerformance {
    ...smartAttributesContentAreaFields
  }
  attributesToShowOnListerGridFeatured {
    ...smartAttributesContentAreaFields
  }
}

fragment smartAttributesContentAreaFields on ContentArea {
  tag
  items {
    componentName
    contentItem {
      __typename
      ... on ImsSystemNameAttributeConfig {
        name
        fieldName
        valueFormat
        entityTypeKey
        clrType
        casingRule
        suffixAttribute {
          ...attributeValueFields
        }
        suffixType
        unitOfMeasure
        label
        disableSorting
        defaultSortOnly
        defaultSortAscending
        sortLabelAscending
        sortLabelDescending
        topSortedAttributeValues {
          ...attributeValueFields
        }
        attributes {
          ...attributeValueFields
        }
        richTextBlocks {
          staticContent
        }
      }
      ... on ImsProductNameAttributeConfig {
        name
        fieldName
        valueFormat
        entityTypeKey
        clrType
        casingRule
        suffixAttribute {
          ...attributeValueFields
        }
        suffixType
        unitOfMeasure
        label
        disableSorting
        defaultSortOnly
        defaultSortAscending
        sortLabelAscending
        sortLabelDescending
        topSortedAttributeValues {
          ...attributeValueFields
        }
        attributes {
          ...attributeValueFields
        }
      }
    }
  }
}

fragment attributeValueFields on AttributeValue {
  value
  hideThisValue
  label
  iconImage {
    id
    workID
    providerName
    isReadOnly
    getPublishedOrLatest
  }
}

fragment imsAssetNameAttributeConfigFields on ImsAssetNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment imsLookNameAttributeConfigFields on ImsLookNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment imsProductItemNameAttributeConfigFields on ImsProductItemNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment imsProductNameAttributeConfigFields on ImsProductNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment imsReferenceCaseNameAttributeConfigFields on ImsReferenceCaseNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment imsSystemNameAttributeConfigFields on ImsSystemNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
  richTextBlocks {
    staticContent
  }
}

fragment imsSystemVariantNameAttributeConfigFields on ImsSystemVariantNameAttributeConfig {
  fieldName
  valueFormat
  entityTypeKey
  clrType
  casingRule
  suffixAttribute {
    ...attributeValueFields
  }
  suffixType
  unitOfMeasure
  label
  disableSorting
  defaultSortOnly
  defaultSortAscending
  sortLabelAscending
  sortLabelDescending
  topSortedAttributeValues {
    ...attributeValueFields
  }
  attributes {
    ...attributeValueFields
  }
}

fragment smartFacetSliderConfigurationFields on SmartFacetSliderConfiguration {
  name
  attributeConfig {
    ...imsAssetNameAttributeConfigFields
    ...imsLookNameAttributeConfigFields
    ...imsProductItemNameAttributeConfigFields
    ...imsProductNameAttributeConfigFields
    ...imsReferenceCaseNameAttributeConfigFields
    ...imsSystemNameAttributeConfigFields
    ...imsSystemVariantNameAttributeConfigFields
  }
  behavior
  placeholder
  label
  enableFilterInfoPanel
  setComparison
  multiSelect
  check
  singleSelect
  precision
  tagline
  secondaryLabel
  stepSize
  minimum
  maximum
}

fragment NumberFacetConfigFields on NumberFacetConfig {
  name
  attributeConfig {
    ...imsAssetNameAttributeConfigFields
    ...imsLookNameAttributeConfigFields
    ...imsProductItemNameAttributeConfigFields
    ...imsProductNameAttributeConfigFields
    ...imsReferenceCaseNameAttributeConfigFields
    ...imsSystemNameAttributeConfigFields
    ...imsSystemVariantNameAttributeConfigFields
  }
  behavior
  placeholder
  label
  enableFilterInfoPanel
  setComparison
  multiSelect
  check
  singleSelect
  stepSize
  minimumValue
  maximumValue
  precision
  populatedPlaceHolder
}

fragment RangeSliderFacetConfigFields on RangeSliderFacetConfig {
  name
  attributeConfig {
    ...imsAssetNameAttributeConfigFields
    ...imsLookNameAttributeConfigFields
    ...imsProductItemNameAttributeConfigFields
    ...imsProductNameAttributeConfigFields
    ...imsReferenceCaseNameAttributeConfigFields
    ...imsSystemNameAttributeConfigFields
    ...imsSystemVariantNameAttributeConfigFields
  }
  behavior
  placeholder
  label
  enableFilterInfoPanel
  setComparison
  multiSelect
  check
  singleSelect
  stepSize
  minimum
  maximum
  precision
}

fragment FacetConfigFields on FacetConfig {
  name
  attributeConfig {
    ...imsAssetNameAttributeConfigFields
    ...imsLookNameAttributeConfigFields
    ...imsProductItemNameAttributeConfigFields
    ...imsProductNameAttributeConfigFields
    ...imsReferenceCaseNameAttributeConfigFields
    ...imsSystemNameAttributeConfigFields
    ...imsSystemVariantNameAttributeConfigFields
  }
  behavior
  placeholder
  label
  enableFilterInfoPanel
  setComparison
  multiSelect
  check
  singleSelect
}

`;

export const GET_CONTENT_HUB_DATA = gql`
query GetContentHubData($filter: String, $page: Int, $pageSize: Int) {
  contentHub(filter: $filter, page: $page, page_size: $pageSize) {
    count
    currentPage
    pageCount
    pageSize
    isLastPage
    totalCount
    list {
      name
      category
      brand
      items
    }
  }
}
`;

export interface Post {
  id: string;
  title: string;
  body: string;
}

export interface GetPostsByUserIdResponse {
  user: {
    posts: Post[];
  } | null | undefined;
}

export interface GetContentHubDataResponse {
  contentHub: {
    list: any[];
  } | null | undefined;
}

export const GET_MY_ACCOUNT_DATA = gql`
query {
	authentication {
    myAccount {
      authenticated
      userName
      firstName
      lastName
      userEmail
    }
  }
}
`;

export interface FavoritesListResponse {
  favorites: {
    list: FavoriteDataResponse[];
  } | null | undefined;
}

export interface FavoriteDataResponse {
  userId: string;
  entityId: string;
  entityTypeKey: string;
}

export interface RefreshTokenResponse {
  authentication: {
    refreshToken: string;
  } | null | undefined;
}

export interface ValidateTokenResponse {
  authentication: {
    validateToken: string;
  } | null | undefined;
}

export interface GetMyAccountDataResponse {
  authentication: {
    myAccount: any;
  } | null | undefined;
}

// {
//   "data": {
//     "authentication": {
//       "myAccount": {
//         "authenticated": true,
//         "userName": "John Doe",
//         "firstName": "John",
//         "lastName": "Doe",
//         "userEmail": "promat.dev@yopmail.com"
//       }
//     }
//   }
// }
